// access domain types. Split out of types/index.ts (2,600 lines) on 2026-07-03.
// Import via '@/types' as before — index.ts re-exports everything.

import type { Capability } from '@/features/access/capabilities.generated';

import type { Shop } from './catalog.types';

// ── Staff / access control ────────────────────────────────────────────────────
export type UserAccountType = 'ACCESS_CONTROLLED' | 'ADMINISTRATION' | 'SUPER_ADMIN'

export type StaffRole = 'OWNER' | 'MANAGER' | 'STAFF'
// Single source of truth: generated from backend/src/shared/utils/capability-catalog.ts
// via `bun run gen:capabilities`. Imported for local use + re-exported so existing
// `@/types` importers keep working.
export type { Capability }

export interface MeResponse {
  id: string
  email: string
  name: string
  avatarUrl?: string | null
  organizationId: string
  type: UserAccountType | null
  role: StaffRole
  roleId: string
  roleName: string
  capabilities: Capability[]
  associatedShopIds: string[]
}

export interface Role {
  id: string
  key: string
  name: string
  capabilities: Capability[]
  isSystem: boolean
}

export interface StaffUser {
  id: string
  name: string
  email: string
  type?: UserAccountType | null
  roleId?: string | null
  phone?: string | null
  avatarUrl?: string | null
  associatedShopIds?: string[]
  lockedAt?: string | null
  lockedReason?: string | null
  personalPinSetAt?: string | null
  workerPermitNumber?: string | null
  workerPermitExpiresAt?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface CreateStaffInput {
  organizationId: string
  name: string
  email: string
  password: string
  type?: UserAccountType
  roleId?: string
  phone?: string
  associatedShopIds?: string[]
  personalPin?: string
  workerPermitNumber?: string | null
  workerPermitExpiresAt?: string | null
}

export interface UpdateStaffInput {
  name?: string
  email?: string
  password?: string
  type?: UserAccountType
  roleId?: string
  phone?: string
  associatedShopIds?: string[]
  personalPin?: string
  lockedAt?: string | null
  workerPermitNumber?: string | null
  workerPermitExpiresAt?: string | null
}

export interface OrganizationWithShops {
  id: string
  slug?: string
  displayName?: string
  avatarUrl?: string | null
  shops: Shop[]
}

export interface User {
  email: string
  name: string
  org: string
}

export interface UserProfile {
  id?: string
  name?: string
  email?: string
  avatarUrl?: string
  org?: string
}

export enum PricingMethod {
  FLAT = 'FLAT',
  WEIGHT_TIER = 'WEIGHT_TIER',
  UNIT_TIER = 'UNIT_TIER',
}

export enum PricingMode {
  SIMPLE = 'SIMPLE',
  TIERED = 'TIERED',
}

export enum TieringMethod {
  WEIGHT_BASED = 'WEIGHT_BASED',
  QUANTITY_BASED = 'QUANTITY_BASED',
}

export interface PricingTier {
  id: string
  pricingSchemeId: string
  startQuantity: string
  unitPriceCents: number
  perUnitPriceCents?: number | null
  createdAt: string
  updatedAt: string
}

export interface PricingScheme {
  id: string
  organizationId: string
  shopId: string
  label: string
  description?: string
  mode: PricingMode
  tieringMethod?: TieringMethod
  pricingMethod?: PricingMethod // Deprecated, keep for backwards compatibility
  weightUomId?: string
  roundingRule?: string
  toleranceValue?: string
  flatPriceCents?: number | null
  isActive: boolean
  isSystemManaged?: boolean
  createdAt: string
  updatedAt: string
  tiers: PricingTier[]
  weightUom?: {
    id: string
    name: string
    shortForm: string
  }
}

export interface MetrcPackage {
  id: string
  shopId: string
  label: string
  packageType: string
  sourceHarvestNames: string
  sourcePackageLabels: string
  itemName: string
  itemCategoryName: string
  itemStrainName: string
  quantity: number
  unitOfMeasureName: string
  unitOfMeasureAbbr: string
  packagedDate: string
  useByDate?: string | null
  labTestingState: string
  labTestingStateDate: string
  isTestingSample: boolean
  isProcessValidationTestingSample: boolean
  productRequiresRemediation: boolean
  containsRemediatedProduct: boolean
  remediationDate: string | null
  receivedFromManifestNumber: string
  receivedFromFacilityLicenses: string
  receivedFromFacilityName: string
  receivedDateTime: string
  isOnHold: boolean
  archivedDate: string | null
  finishedDate: string | null
  status: string
  locationId?: string | null
  locationName?: string | null
  productId?: string | null
  saleConfigId?: string | null
  metrcItemId?: string | null
  createdAt: string
  updatedAt: string
  product?: { id: string; name: string; images?: string[] } | null
  saleConfig?: { id: string; name: string; sku: string } | null
  metrcItem?: { id: string; name: string; productId: string | null; product?: { id: string; name: string } | null } | null
  unitOfMeasurement?: { id: string; name: string; shortForm: string; slug: string; measurementType: 'WEIGHT' | 'COUNT' | 'VOLUME' } | null
}

export type AvailabilityState = 'AVAILABLE' | 'PARTIALLY_AVAILABLE' | 'UNAVAILABLE' | 'NO_STOCK'

export interface UnsellableReason {
  code: string
  label: string
  resolvable: boolean
  resolveAction?: string
}

export interface PackageBreakdown {
  total: number
  sellable: number
  unsellable: number
  unsellableReasonSummary: string[]
}

export interface UnifiedPackage {
  id: string
  label: string
  quantity: string | number
  quantityUom?: string
  quantityInSellUom?: string | number
  sellUom?: string
  originalQuantity?: string | number
  unitOfMeasureAbbr?: string
  packagedDate?: string
  useByDate?: string
  labTestingState?: string
  sellable: boolean
  unsellableReasons: UnsellableReason[]
}

export interface UnifiedInventoryItem {
  saleConfigId: string
  localInventoryId?: string | null
  /** Local records: no history beyond creation, so it may be deleted. */
  deletable?: boolean
  productId?: string | null
  productName: string
  categoryId?: string | null
  sku?: string | null
  displayName?: string | null
  uom?: string | null
  uomName?: string | null
  pricingSchemeId?: string | null
  pricingSchemeName?: string | null
  pricingSchemeType?: string | null
  /// Server-enriched (2026-07-11) so the stock table renders price labels,
  /// tier hovers and strain chips without a per-row GET /sale-configs/:id.
  sellType?: string | null
  pricingTiers?: { startQuantity: number; unitPriceCents: number }[]
  strainName?: string | null
  strainType?: string | null
  unitPriceCents?: number | null
  available?: number | null
  isTrackingEnabled?: boolean
  lowStockThreshold?: number | null
  track: 'local' | 'metrc'
  status?: string | null
  packages?: UnifiedPackage[]
  imageUrl?: string | null
  isOnHold?: boolean
  holdReason?: string
  heldAt?: string
  availability?: {
    state: string
    availableQuantity: number
    blockedQuantity: number
    totalPackageCount: number
    sellablePackageCount: number
    blockReasons: string[]
  }
}
